import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useCompanies, Company } from "@/hooks/useCompanies";
import { usePeople, Person } from "@/hooks/usePeople";
import {
  ProjectGroupInsert,
  ProjectGroupUpdate,
} from "@/hooks/useProjectGroups";
import { Database } from "@/integrations/neon/types";

type ProjectGroupStatus = Database["public"]["Enums"]["project_group_status"];

const COLORS = [
  { value: "blue", label: "Blue", class: "bg-blue-500" },
  { value: "green", label: "Green", class: "bg-green-500" },
  { value: "yellow", label: "Yellow", class: "bg-yellow-500" },
  { value: "red", label: "Red", class: "bg-red-500" },
  { value: "purple", label: "Purple", class: "bg-purple-500" },
  { value: "orange", label: "Orange", class: "bg-orange-500" },
  { value: "pink", label: "Pink", class: "bg-pink-500" },
];

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  customer_id: z.string().nullable().optional(),
  contact_id: z.string().nullable().optional(),
  status: z.enum(["active", "archived"] as const).default("active"),
  color: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ProjectGroupFormProps {
  defaultValues?: Partial<FormValues>;
  onSubmit: (data: ProjectGroupInsert | ProjectGroupUpdate) => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
}

export function ProjectGroupForm({
  defaultValues,
  onSubmit,
  isLoading,
  isReadOnly,
}: ProjectGroupFormProps) {
  const { data: companies } = useCompanies();
  const { data: people } = usePeople();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name || "",
      description: defaultValues?.description || "",
      customer_id: defaultValues?.customer_id || null,
      contact_id: defaultValues?.contact_id || null,
      status: (defaultValues?.status as ProjectGroupStatus) || "active",
      color: defaultValues?.color || null,
    },
  });

  const handleSubmit = (values: FormValues) => {
    onSubmit({
      name: values.name,
      description: values.description || null,
      customer_id: values.customer_id || null,
      contact_id: values.contact_id || null,
      status: values.status as ProjectGroupStatus,
      color: values.color || null,
    });
  };

  const companyOptions =
    companies?.map((c: Company) => ({
      value: c.id,
      label: c.name,
    })) || [];

  const contactOptions =
    people?.map((p: Person) => ({
      value: p.id,
      label: `${p.first_name} ${p.last_name}`,
    })) || [];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Project group name"
                  disabled={isReadOnly}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Optional description"
                  disabled={isReadOnly}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="customer_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer</FormLabel>
              <FormControl>
                <EntityCombobox
                  options={companyOptions}
                  value={field.value || ""}
                  onChange={field.onChange}
                  placeholder="Select customer..."
                  disabled={isReadOnly}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="contact_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contact</FormLabel>
              <FormControl>
                <EntityCombobox
                  options={contactOptions}
                  value={field.value || ""}
                  onChange={field.onChange}
                  placeholder="Select contact..."
                  disabled={isReadOnly}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Color</FormLabel>
              <FormControl>
                <Select
                  value={field.value || ""}
                  onValueChange={field.onChange}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a color..." />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-4 h-4 rounded-full ${color.class}`}
                          />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isReadOnly && (
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        )}
      </form>
    </Form>
  );
}
